<template>
  <div class="row mx-0 px-3 py-4 border-top gap-3">
    <div
      v-for="(value, index) in optionalInformation.resume_languages"
      :key="index"
      class="input-group pe-0"
    >
      <label class="form-label size-12" for="">外国語{{ index + 1 }}</label>
      <div class="d-flex w-100 gap-1 gap-md-3">
        <Field
          v-slot="{ field }"
          :name="`language-${index}`"
          :model-value="value.other_language_id"
        >
          <div class="col-md-4 col-5 pe-1">
            <select
              class="form-select select-input-primary p-0d8 size-14 pe-4"
              v-bind="field"
              @change="onChangeLanguageId($event, value, index)"
            >
              <option :value="0" disabled selected></option>
              <option
                v-for="language in filteredLanguages(index)"
                :key="language.id"
                :value="language.id"
              >
                {{ language.name }}
              </option>
            </select>
          </div>
        </Field>
        <Field
          v-slot="{ field }"
          :name="`language-level-${index}`"
          :model-value="value.other_language_level_id"
        >
          <div class="col-md-4 col-5 pe-0">
            <select
              class="form-select select-input-primary size-14 p-0d8"
              v-bind="field"
              @change="onChangeLevelId($event, value, index)"
            >
              <option :value="0" disabled selected></option>
              <option
                v-for="languageLevel in languageLevels"
                :key="languageLevel.id"
                :value="languageLevel.id"
              >
                {{ languageLevel.name }}
              </option>
            </select>
          </div>
        </Field>
        <div class="d-flex justify-content-center align-items-center flex-column ms-2">
          <img
            alt="Remove icon"
            class="h-29x w-29x cursor-pointer"
            src="@/assets/images/icons/remove-icon.svg"
            @click="() => removeItemForResumeLanguage(index)"
          />
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-center w-100 m-0">
      <button
        type="button"
        class="btn btn-outline-secondary d-flex justify-content-center align-items-center size-12 w-12 p-0d8 rounded-5 h-36x"
        @click="addItemForResumeLanguage"
      >
        <div class="icon w-1"></div>
        <span class="ms-2">語学を追加する</span>
      </button>
    </div>
    <div class="input-group row pe-0">
      <div class="col-12 pe-0">
        <label for="language-remarks" class="form-label col-md-12 px-0 size-12 lh-12">
          語学備考欄
        </label>
        <Field
          v-slot="{ field, errors }"
          v-model="optionalInformation.language_remarks"
          name="language_remarks"
        >
          <textarea
            class="form-control bg-silver-primary input-primary size-14 lh-16"
            rows="5"
            placeholder="語学について補足があればご入力ください。"
            :class="{ 'is-invalid': !!errors.length }"
            v-bind="field"
          ></textarea>
          <div
            class="d-flex justify-content-end px-0 size-10"
            :class="
              optionalInformation.language_remarks?.length > 500 ? 'text-danger' : 'text-black-50'
            "
          >
            {{ optionalInformation.language_remarks?.length || 0 }}
            / 500（最大文字数500文字）
          </div>
          <ErrorMessage name="language_remarks" class="invalid-feedback" />
        </Field>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Field, useFieldArray, ErrorMessage } from 'vee-validate';
import { onMounted, ref } from 'vue';
import { LanguageType } from '@/types/models/LanguageType';
import { LanguageLevelType } from '@/types/models/LanguageLevel';
import { getMasterLanguage, getMasterLanguageLevel } from '@/apis/candidate';
import { useOptionalInformationStore } from '@/stores/resume-candidate/OptionalInformation.store';

const languages = ref<LanguageType[]>([]);
const languageLevels = ref<LanguageLevelType[]>([]);
const optionalInformation = useOptionalInformationStore();
const { update }: any = useFieldArray('profile_resume_occupations');

onMounted(() => {
  loadLanguages();
  loadMasterLanguageLevel();
});

const filteredLanguages = (index: number): LanguageType[] => {
  return languages.value.filter((language) => {
    return !optionalInformation.resume_languages.some(
      (resume_language, i) =>
        (resume_language.other_language_id === language.id && i !== index) ||
        optionalInformation.native_language_id === language.id
    );
  });
};

const loadLanguages = async (): Promise<void> => {
  const res = await getMasterLanguage();
  if (res.status === 200) {
    languages.value = res.data;
  }
};

const loadMasterLanguageLevel = async (): Promise<void> => {
  const response = await getMasterLanguageLevel();
  if (response.status === 200) languageLevels.value = response.data;
};

const addItemForResumeLanguage = (): void => {
  optionalInformation.resume_languages.push({
    other_language_id: 0,
    other_language_level_id: 0,
    index: new Date().getTime(),
  });
};

const removeItemForResumeLanguage = (index: number): void => {
  optionalInformation.resume_languages.splice(index, 1);
};

const onChangeLanguageId = ($event: any, resume_languages: any, index: number): void => {
  update(index, { ...resume_languages, occupation_id: $event.target.value });
  optionalInformation.resume_languages[index].other_language_id = +$event.target.value;
};

const onChangeLevelId = ($event: any, resume_languages: any, index: number): void => {
  update(index, { ...resume_languages, years_of_experience: $event.target.value });
  optionalInformation.resume_languages[index].other_language_level_id = +$event.target.value;
};
</script>
